import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, TrendingUp, DollarSign, ShoppingBag, Eye,
  Package, Star, BarChart3, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SellerAnalytics() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/seller/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Analytics error:', error);
      if (error.response?.status === 403) {
        toast.error('Only creators can access seller analytics');
        navigate('/dashboard');
      } else {
        setError('Failed to load analytics. Please try again.');
        toast.error('Failed to load analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#00F0FF] mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Unable to Load Analytics</h2>
          <p className="text-neutral-400 mb-4">{error}</p>
          <Button onClick={loadAnalytics} className="bg-[#00F0FF] text-black">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No Analytics Data</h2>
          <p className="text-neutral-400">Start selling products to see your analytics!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
            <Logo />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-12 py-6 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-3">
            <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-[#00F0FF]" />
            <h1 className="text-xl md:text-3xl font-bold">Analytics</h1>
          </div>
          
          {/* Period Selector */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
            <div className="flex gap-2 min-w-max md:min-w-0">
              {['7d', '30d', '90d', 'all'].map((p) => (
                <Button
                  key={p}
                  variant={period === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPeriod(p)}
                  className={`${period === p ? 'bg-[#00F0FF] text-black' : 'border-white/20'} text-sm whitespace-nowrap`}
                >
                  {p === 'all' ? 'All' : p}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide mb-6 md:mb-8">
          <div className="flex md:grid md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 min-w-max md:min-w-0">
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[110px] md:w-auto flex-shrink-0 md:flex-shrink">
              <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-[#00F0FF] mb-1 md:mb-2" />
              <p className="text-lg md:text-2xl font-bold">${analytics.summary.total_revenue.toFixed(2)}</p>
              <p className="text-xs text-neutral-400">Revenue</p>
            </div>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[110px] md:w-auto flex-shrink-0 md:flex-shrink">
              <ShoppingBag className="w-4 h-4 md:w-5 md:h-5 text-white mb-1 md:mb-2" />
              <p className="text-lg md:text-2xl font-bold">{analytics.summary.total_sales}</p>
              <p className="text-xs text-neutral-400">Sales</p>
            </div>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[110px] md:w-auto flex-shrink-0 md:flex-shrink">
              <Package className="w-4 h-4 md:w-5 md:h-5 text-[#00F0FF] mb-1 md:mb-2" />
              <p className="text-lg md:text-2xl font-bold">{analytics.summary.total_units}</p>
              <p className="text-xs text-neutral-400">Units</p>
            </div>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[110px] md:w-auto flex-shrink-0 md:flex-shrink">
              <Eye className="w-4 h-4 md:w-5 md:h-5 text-[#FF6B6B] mb-1 md:mb-2" />
              <p className="text-lg md:text-2xl font-bold">{analytics.summary.views}</p>
              <p className="text-xs text-neutral-400">Views</p>
            </div>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[110px] md:w-auto flex-shrink-0 md:flex-shrink">
              <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-[#10B981] mb-1 md:mb-2" />
              <p className="text-lg md:text-2xl font-bold">{analytics.summary.conversion_rate}%</p>
              <p className="text-xs text-neutral-400">Conv.</p>
            </div>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-3 md:p-4 w-[110px] md:w-auto flex-shrink-0 md:flex-shrink">
              <Package className="w-4 h-4 md:w-5 md:h-5 text-neutral-400 mb-1 md:mb-2" />
              <p className="text-lg md:text-2xl font-bold">{analytics.summary.total_products}</p>
              <p className="text-xs text-neutral-400">Products</p>
            </div>
          </div>
        </div>

        {/* Revenue Chart */}
        {analytics.revenue_chart && analytics.revenue_chart.length > 0 && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 mb-8">
            <h2 className="text-lg font-bold mb-4">Revenue Over Time</h2>
            
            {/* Show different view if only 1-2 data points */}
            {analytics.revenue_chart.length <= 2 ? (
              <div className="space-y-4">
                {analytics.revenue_chart.map((day, index) => (
                  <div key={`rev-day-${day.date || index}`} className="bg-black/30 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-neutral-400">{day.date}</p>
                        <p className="text-2xl font-bold text-[#00F0FF]">${day.revenue.toFixed(2)}</p>
                      </div>
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center">
                        <DollarSign className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-neutral-500 text-center">
                  More data points will show a chart as you make more sales
                </p>
              </div>
            ) : (
              <>
                <div className="h-64 flex items-end gap-2 bg-black/20 rounded-lg p-4">
                  {analytics.revenue_chart.map((day, index) => {
                    const maxRevenue = Math.max(...analytics.revenue_chart.map(d => d.revenue), 1);
                    const height = maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0;
                    return (
                      <div
                        key={`rev-bar-${day.date || index}`}
                        className="flex-1 max-w-[60px] bg-gradient-to-t from-[#00F0FF] to-white rounded-t hover:opacity-80 transition-opacity relative group cursor-pointer"
                        style={{ height: `${Math.max(height, 5)}%`, minHeight: '8px' }}
                      >
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black border border-white/20 px-2 py-1 rounded text-xs whitespace-nowrap z-10">
                          ${day.revenue.toFixed(2)}
                          <br />
                          <span className="text-neutral-400">{day.date}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* X-axis labels */}
                <div className="flex justify-between mt-2 text-xs text-neutral-500">
                  <span>{analytics.revenue_chart[0]?.date}</span>
                  <span>{analytics.revenue_chart[analytics.revenue_chart.length - 1]?.date}</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Top Products */}
        <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4">Top Performing Products</h2>
          {analytics.top_products.length === 0 ? (
            <p className="text-neutral-400 text-center py-8">No sales data yet</p>
          ) : (
            <div className="space-y-4">
              {analytics.top_products.map((product, index) => (
                <div
                  key={product.product_id}
                  className="flex items-center gap-4 p-4 bg-black/30 rounded-xl hover:bg-black/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/product/${product.product_id}`)}
                >
                  <span className="text-2xl font-bold text-neutral-600 w-8">#{index + 1}</span>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-neutral-800 flex items-center justify-center">
                      <Package className="w-6 h-6 text-neutral-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{product.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-neutral-400">
                      <Star className="w-3 h-3 text-yellow-500" />
                      {product.rating.toFixed(1)} ({product.reviews} reviews)
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#00F0FF]">${product.revenue.toFixed(2)}</p>
                    <p className="text-xs text-neutral-400">{product.units} units sold</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default SellerAnalytics;
